import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";
import SearchSelectInput from "../../../components/comman/SearchSelectInput";

export default function CreateGRNModal({ isOpen, toggle, defaultSiteId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Core Form State
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(defaultSiteId || "");
  const [selectedSiteName, setSelectedSiteName] = useState("");
  const [isLoadingSite, setIsLoadingSite] = useState(false);

  const [poList, setPoList] = useState([]);
  const [poOptions, setPoOptions] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [selectedPoName, setSelectedPoName] = useState("");
  const [isLoadingPo, setIsLoadingPo] = useState(false);

  const [selectedPoDetail, setSelectedPoDetail] = useState(null);
  const [grnDate, setGrnDate] = useState(moment().format("YYYY-MM-DD"));
  const [financialYear, setFinancialYear] = useState("--");
  const [remarks, setRemarks] = useState("");
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Expense heads and items from PO
  const [expenseHeads, setExpenseHeads] = useState([]);

  // Available items for sub-item substitution
  const [availableItems, setAvailableItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [subItemModalOpen, setSubItemModalOpen] = useState(false);
  const [subItemTarget, setSubItemTarget] = useState({ headIdx: null, itemIdx: null, itemName: "" });
  const [subItemSelection, setSubItemSelection] = useState({ itemId: "", itemName: "", quantity: "" });

  // Dedicated Machinery Form Modal State
  const [machineryModalOpen, setMachineryModalOpen] = useState(false);
  const [machineryModalTarget, setMachineryModalTarget] = useState({
    headIdx: null,
    itemIdx: null,
    itemName: "",
    itemCode: "",
  });
  const [activeUnitTab, setActiveUnitTab] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableItems();
      fetchSites().then((loadedSites) => {
        if (defaultSiteId && Array.isArray(loadedSites)) {
          const match = loadedSites.find((s) => String(s.value || s.id) === String(defaultSiteId));
          if (match) {
            setSelectedSiteId(match.value || match.id);
            setSelectedSiteName(match.label || match.name);
          }
        }
      });
    } else {
      resetForm();
    }
  }, [isOpen, defaultSiteId]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchPurchaseOrders(selectedSiteId);
    } else {
      setPoList([]);
      setPoOptions([]);
      setSelectedPoId("");
      setSelectedPoName("");
      setSelectedPoDetail(null);
      setExpenseHeads([]);
    }
  }, [selectedSiteId]);

  const resetForm = () => {
    setSelectedSiteId("");
    setSelectedSiteName("");
    setSelectedPoId("");
    setSelectedPoName("");
    setSelectedPoDetail(null);
    setExpenseHeads([]);
    setPoList([]);
    setPoOptions([]);
    setRemarks("");
    setFiles([]);
    setGrnDate(moment().format("YYYY-MM-DD"));
    setFinancialYear("--");
    setFormErrors({});
  };

  const fetchSites = async (inputValue = "") => {
    setIsLoadingSite(true);
    try {
      const searchStr = typeof inputValue === "string" ? inputValue.trim() : "";
      const searchParam = searchStr ? `?search=${encodeURIComponent(searchStr)}` : "";

      // Follow createPurchaseRequest pattern: fetch assigned user sites
      let res = await callApi(`${apiUrl.get_user_site}${searchParam}`, { method: "GET" });
      let list = [];
      if (res?.status === true && Array.isArray(res?.data?.site) && res.data.site.length > 0) {
        list = res.data.site.map((item) => {
          const siteObj = item?.site || item;
          const sName = siteObj?.name || item?.name || "Site";
          const sId = siteObj?.id || item?.id;
          return {
            ...item,
            id: sId,
            name: sName,
            label: sName,
            value: sId,
            site: siteObj,
          };
        });
      } else {
        // Fallback to get_site if get_user_site is empty or unassigned
        const fallbackRes = await callApi(`${apiUrl.get_site}${searchParam}`, { method: "GET" });
        if (fallbackRes?.status === true) {
          const raw = Array.isArray(fallbackRes?.data?.site)
            ? fallbackRes.data.site
            : Array.isArray(fallbackRes?.data)
              ? fallbackRes.data
              : [];
          list = raw.map((item) => {
            const siteObj = item?.site || item;
            const sName = siteObj?.name || item?.name || "Site";
            const sId = siteObj?.id || item?.id;
            return {
              ...item,
              id: sId,
              name: sName,
              label: sName,
              value: sId,
              site: siteObj,
            };
          });
        }
      }
      setSites(list);
      return list;
    } catch (e) {
      console.error("fetchSites error:", e);
      setSites([]);
      return [];
    } finally {
      setIsLoadingSite(false);
    }
  };

  const fetchAvailableItems = async (search = "") => {
    setIsLoadingItems(true);
    try {
      const searchStr = typeof search === "string" ? search.trim() : "";
      const searchParam = searchStr ? `&search=${encodeURIComponent(searchStr)}` : "";
      const res = await callApi(`${apiUrl.get_items}all=true${searchParam}`, { method: "GET" });
      if (res?.status === true) {
        const raw = res?.data?.items || res?.data?.item || res?.data || [];
        const rawList = Array.isArray(raw) ? raw : [];
        setAvailableItems(rawList);
        const formatted = rawList.map((it) => {
          const itemCode = it.itemCode ? ` (${it.itemCode})` : "";
          const unit = it.unit ? ` [${it.unit}]` : "";
          const subLabelParts = [
            it.itemCode ? `Code: ${it.itemCode}` : null,
            it.unit ? `Unit: ${it.unit}` : null,
            it.standardRate ? `Rate: ₹${it.standardRate}` : (it.rate ? `Rate: ₹${it.rate}` : null),
            it.hsnSac ? `HSN/SAC: ${it.hsnSac}` : null,
          ].filter(Boolean);

          return {
            ...it,
            id: it.id,
            value: it.id,
            label: `${it.name}${itemCode}${unit}`,
            subLabel: subLabelParts.join(" • "),
          };
        });
        setItemOptions(formatted);
        return formatted;
      }
    } catch (e) {
      console.error("fetchAvailableItems error:", e);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const fetchPurchaseOrders = async (siteId, inputValue = "") => {
    if (!siteId) {
      setPoList([]);
      setPoOptions([]);
      return [];
    }
    setIsLoadingPo(true);
    try {
      const searchStr = typeof inputValue === "string" ? inputValue.trim() : "";
      const searchParam = searchStr ? `&search=${encodeURIComponent(searchStr)}` : "";
      const url = `${apiUrl.get_purchase}from=grn&type=supplyRequest&siteId=${siteId}${searchParam}`;
      const res = await callApi(url, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.purchase) ? res.data.purchase : [];
        setPoList(list);
        const formatted = list.map((po) => {
          const poNum = po.purchaseNumber || `PO #${po.id}`;
          const fy = po.financialYear ? ` (${po.financialYear})` : "";
          const vendorName = po?.vendor?.firmName || po?.vendor?.name ? `Vendor: ${po?.vendor?.firmName || po?.vendor?.name}` : "";
          const total = po?.totalAmount ? `Total: ₹${parseFloat(po.totalAmount).toLocaleString("en-IN")}` : "";
          const subLabelParts = [vendorName, total].filter(Boolean);

          return {
            ...po,
            id: po.id,
            value: po.id,
            label: `${poNum}${fy}`,
            subLabel: subLabelParts.join(" • "),
          };
        });
        setPoOptions(formatted);
        return formatted;
      } else {
        setPoList([]);
        setPoOptions([]);
        return [];
      }
    } catch (e) {
      console.error("fetchPurchaseOrders error:", e);
      setPoList([]);
      setPoOptions([]);
      return [];
    } finally {
      setIsLoadingPo(false);
    }
  };

  const handleSelectSite = (option) => {
    const siteId = option?.value || "";
    const siteName = option?.label || "";
    setSelectedSiteId(siteId);
    setSelectedSiteName(siteName);
    setSelectedPoId("");
    setSelectedPoName("");
    setSelectedPoDetail(null);
    setExpenseHeads([]);
    setFinancialYear("--");
    setFormErrors((prev) => ({ ...prev, site: "" }));
  };

  const handleSelectPOOption = (option) => {
    const poId = option?.value || "";
    const poLabel = option?.label || "";
    setSelectedPoName(poLabel);
    setFormErrors((prev) => ({ ...prev, po: "" }));
    handleSelectPO(poId);
  };

  const handleSelectPO = async (poId) => {
    setSelectedPoId(poId);
    if (!poId) {
      setSelectedPoName("");
      setSelectedPoDetail(null);
      setExpenseHeads([]);
      setFinancialYear("--");
      return;
    }

    setLoading(true);
    try {
      const url = `${apiUrl.get_purchase_detail}purchaseId=${poId}`;
      const res = await callApi(url, { method: "GET" });
      if (res?.status === true && res?.data) {
        const poData = res.data;
        setSelectedPoDetail(poData);
        setFinancialYear(poData.financialYear || "--");

        const heads = (poData?.purchaserequest?.purchaserequestexpenseheads || []).map((eh) => {
          const items = (eh?.supplyrequestitems || []).map((it) => {
            const itemRecord = it?.item || {};
            const isMachinery =
              itemRecord.stockType === 0 ||
              itemRecord.stockType === "0" ||
              itemRecord.stockType === 2 ||
              itemRecord.stockType === "2" ||
              itemRecord.isSerialized === true ||
              (eh?.expensehead?.name || "").toLowerCase().includes("machin") ||
              (eh?.expensehead?.name || "").toLowerCase().includes("asset") ||
              (itemRecord.name || "").toLowerCase().includes("jcb") ||
              (itemRecord.name || "").toLowerCase().includes("excavator") ||
              (itemRecord.name || "").toLowerCase().includes("tractor") ||
              (itemRecord.name || "").toLowerCase().includes("crane") ||
              (itemRecord.name || "").toLowerCase().includes("generator");

            const orderedQty = parseFloat(it?.updatedQuantity || 0);
            const receivedQty = parseFloat(it?.receivedQuantity || 0);
            const remainingQty = Math.max(0, orderedQty - receivedQty);

            return {
              poItemId: it?.id,
              itemId: it?.itemId || itemRecord?.id,
              name: itemRecord?.name || "Material Item",
              itemCode: itemRecord?.itemCode || "ITEM",
              unit: itemRecord?.unit || "Nos",
              make: itemRecord?.make || "",
              model: itemRecord?.model || "",
              standardRate: itemRecord?.standardRate || 0,
              isMachinery,
              orderedQty,
              receivedQty,
              remainingQty,
              receiveQty: "",
              subItems: [],
              machineUnits: [],
              showInlineUnits: false,
            };
          });

          return {
            expenseHeadId: eh?.expenseHeadId,
            name: eh?.expensehead?.name || "General Expense Head",
            items,
          };
        });

        setExpenseHeads(heads);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load PO details");
    } finally {
      setLoading(false);
    }
  };

  const handleItemQtyChange = (headIdx, itemIdx, val) => {
    const updated = [...expenseHeads];
    const targetItem = updated[headIdx].items[itemIdx];

    const cleanVal = val.replace(/[^0-9.]/g, "");
    targetItem.receiveQty = cleanVal;

    const numQty = parseFloat(cleanVal) || 0;

    // If machinery, dynamically prepare unit registration records
    if (targetItem.isMachinery) {
      const count = Math.max(0, Math.floor(numQty));
      const currentUnits = targetItem.machineUnits || [];
      const newUnits = [];

      const siteObj = sites.find((s) => (s.id || s.value) === selectedSiteId);
      const siteName = siteObj?.label || siteObj?.name || selectedSiteName || "SITE";
      const siteCode = siteName.substring(0, 4).toUpperCase().replace(/\s+/g, "");

      for (let i = 0; i < count; i++) {
        if (currentUnits[i]) {
          newUnits.push(currentUnits[i]);
        } else {
          const random4 = Math.floor(1000 + Math.random() * 9000);
          newUnits.push({
            unitIndex: i + 1,
            name: targetItem.name,
            assetNo: `${targetItem.itemCode || "EQ"}-${siteCode}-${random4}${count > 1 ? `-${i + 1}` : ""}`,
            machineNumber: "",
            chesisNumber: "",
            engineNumber: "",
            readingType: "Hours",
            initialReading: "0",
            fuelType: "Diesel",
            model: targetItem.model || "",
            make: targetItem.make || "",
            status: "Operational",
          });
        }
      }
      targetItem.machineUnits = newUnits;
    }

    setExpenseHeads(updated);
  };

  const handleMachineUnitFieldChange = (headIdx, itemIdx, unitIdx, field, value) => {
    const updated = [...expenseHeads];
    if (updated[headIdx]?.items[itemIdx]?.machineUnits?.[unitIdx]) {
      updated[headIdx].items[itemIdx].machineUnits[unitIdx][field] = value;
      setExpenseHeads(updated);
    }
  };

  // Open dedicated machinery modal
  const openMachineryModal = (headIdx, itemIdx, itemName, itemCode) => {
    const targetItem = expenseHeads[headIdx]?.items[itemIdx];
    if (!targetItem?.receiveQty || parseFloat(targetItem.receiveQty) <= 0) {
      toast.info("Please enter the number of machinery units received first.");
      return;
    }
    setMachineryModalTarget({ headIdx, itemIdx, itemName, itemCode });
    setActiveUnitTab(0);
    setMachineryModalOpen(true);
  };

  const toggleInlineUnits = (headIdx, itemIdx) => {
    const updated = [...expenseHeads];
    if (updated[headIdx]?.items[itemIdx]) {
      updated[headIdx].items[itemIdx].showInlineUnits = !updated[headIdx].items[itemIdx].showInlineUnits;
      setExpenseHeads(updated);
    }
  };

  const openSubItemDialog = (headIdx, itemIdx, itemName) => {
    setSubItemTarget({ headIdx, itemIdx, itemName });
    setSubItemSelection({ itemId: "", itemName: "", quantity: "" });
    setSubItemModalOpen(true);
    if (itemOptions.length === 0) {
      fetchAvailableItems();
    }
  };

  const handleSelectSubItem = (option) => {
    setSubItemSelection((prev) => ({
      ...prev,
      itemId: option?.value || "",
      itemName: option?.label || "",
    }));
  };

  const handleAddSubItem = () => {
    if (!subItemSelection.itemId) {
      toast.error("Please select a substitute sub-item");
      return;
    }
    if (!subItemSelection.quantity || parseFloat(subItemSelection.quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    const { headIdx, itemIdx } = subItemTarget;
    if (headIdx === null || itemIdx === null) return;

    const itemObj = availableItems.find((it) => (it.id || it.value) === subItemSelection.itemId);
    const updated = [...expenseHeads];
    const targetItem = updated[headIdx].items[itemIdx];

    const existing = targetItem.subItems || [];
    if (existing.some((s) => (s.itemId || s.value) === subItemSelection.itemId)) {
      toast.error("This sub-item is already added.");
      return;
    }

    targetItem.subItems = [
      ...existing,
      {
        itemId: subItemSelection.itemId,
        name: itemObj?.name || subItemSelection.itemName || "Sub-Item Component",
        quantity: parseFloat(subItemSelection.quantity),
        isSubItem: true,
      },
    ];
    targetItem.receiveQty = ""; // clear parent direct quantity when substituted

    setExpenseHeads(updated);
    setSubItemSelection({ itemId: "", itemName: "", quantity: "" });
    toast.success("Sub-item component added.");
  };

  const handleRemoveSubItem = (headIdx, itemIdx, subIdx) => {
    const updated = [...expenseHeads];
    const targetItem = updated[headIdx].items[itemIdx];
    targetItem.subItems = (targetItem.subItems || []).filter((_, idx) => idx !== subIdx);
    setExpenseHeads(updated);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFiles((prev) => [...prev, file]);
    }
    e.target.value = "";
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmitGRN = async (e) => {
    e.preventDefault();

    if (!selectedSiteId) {
      setFormErrors((prev) => ({ ...prev, site: "Please select a site" }));
      toast.error("Please select a site");
      return;
    }
    if (!selectedPoId) {
      setFormErrors((prev) => ({ ...prev, po: "Please select a Purchase Order" }));
      toast.error("Please select a Purchase Order");
      return;
    }

    // Build grnhead payload
    const grnhead = [];
    const machineryToRegister = [];

    expenseHeads.forEach((head) => {
      const grnItem = [];

      (head.items || []).forEach((it) => {
        const hasSubItems = it.subItems && it.subItems.length > 0;

        if (hasSubItems) {
          it.subItems.forEach((sub) => {
            if (sub.itemId && parseFloat(sub.quantity) > 0) {
              grnItem.push({
                itemId: sub.itemId,
                quantity: parseFloat(sub.quantity),
                poItemId: it.poItemId || it.itemId,
                isSubItem: true,
              });
            }
          });
        } else if (it.receiveQty && parseFloat(it.receiveQty) > 0) {
          const qty = parseFloat(it.receiveQty);
          grnItem.push({
            itemId: it.itemId,
            quantity: qty,
            poItemId: it.poItemId || it.itemId,
            isSubItem: false,
          });

          // If machinery item, collect units
          if (it.isMachinery && it.machineUnits && it.machineUnits.length > 0) {
            it.machineUnits.forEach((unit) => {
              machineryToRegister.push({
                ...unit,
                siteId: selectedSiteId,
                vendorId: selectedPoDetail?.vendorId || null,
                poNumber: selectedPoDetail?.purchaseNumber || null,
                purchaseCost: it.standardRate || null,
                purchaseDate: grnDate,
              });
            });
          }
        }
      });

      if (grnItem.length > 0) {
        grnhead.push({
          expenseHeadId: head.expenseHeadId,
          grnItem,
        });
      }
    });

    const totalValidItems = grnhead.reduce((acc, h) => acc + h.grnItem.length, 0);
    if (totalValidItems === 0) {
      toast.error("Please enter received quantity for at least one item.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("siteId", selectedSiteId);
      formData.append("purchaseId", selectedPoId);
      formData.append("date", grnDate);
      formData.append("description", remarks);
      formData.append("grnHead", JSON.stringify(grnhead));

      files.forEach((f) => formData.append(f.name, f));

      // 1. Submit GRN
      const grnRes = await callApi(apiUrl.create_GRN, {
        method: "POST",
        form_data: formData,
      });

      if (grnRes?.status === true) {
        // 2. Register machinery units if any detailed machines were configured
        let registeredCount = 0;
        if (machineryToRegister.length > 0) {
          for (const m of machineryToRegister) {
            try {
              const machPayload = {
                name: m.name,
                assetNo: m.assetNo,
                machineNumber: m.machineNumber || null,
                chesisNumber: m.chesisNumber || null,
                engineNumber: m.engineNumber || null,
                readingType: m.readingType || "Hours",
                fuelType: m.fuelType || "Diesel",
                model: m.model || null,
                make: m.make || null,
                siteId: m.siteId,
                vendorId: m.vendorId,
                poNumber: m.poNumber,
                purchaseCost: m.purchaseCost,
                purchaseDate: m.purchaseDate,
                status: m.status || "Operational",
              };

              const createdMach = await callApi(apiUrl.add_machinery, {
                method: "POST",
                data: machPayload,
              });

              if (createdMach?.status === true && createdMach?.data?.id) {
                registeredCount++;
                const initRead = parseFloat(m.initialReading) || 0;
                if (initRead > 0) {
                  await callApi(apiUrl.add_machinery_reading, {
                    method: "POST",
                    data: {
                      machineryId: createdMach.data.id,
                      reading: initRead,
                      date: grnDate,
                      readingType: m.readingType || "Hours",
                    },
                  });
                }
              }
            } catch (err) {
              console.warn("Machinery registration unit warning:", err);
            }
          }
        }

        const msg = registeredCount > 0
          ? `GRN Created Successfully! Received items into stock & registered ${registeredCount} machinery units into Fleet.`
          : "Goods Received Note (GRN) created successfully!";

        toast.success(msg);
        resetForm();
        toggle();
        onSuccess?.();
      } else {
        toast.error(grnRes?.message || "Failed to create GRN.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const activeMachineryItem =
    machineryModalTarget.headIdx !== null && machineryModalTarget.itemIdx !== null
      ? expenseHeads[machineryModalTarget.headIdx]?.items[machineryModalTarget.itemIdx]
      : null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
        <form onSubmit={handleSubmitGRN}>
          <ModalHeader
            toggle={toggle}
            close={
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label="Close"
                style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
              />
            }
            style={{ background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
              <span style={{ fontSize: "18px" }}>📥</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", letterSpacing: "-0.01em" }}>
                  Goods Received Note (GRN) & Asset Inward
                </div>
                <div style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 400 }}>
                  Unified Inward Receiving: Consumables directly to Stock & Machinery units to Fleet Master
                </div>
              </div>
            </div>
          </ModalHeader>

          <ModalBody style={{ padding: "1.5rem", maxHeight: "78vh", overflowY: "auto", background: "#f8fafc" }}>
            {/* Top Controls Grid */}
            <div className="card border-0 shadow-sm rounded-3 p-3 mb-4" style={{ background: "#ffffff", overflow: "visible" }}>
              <div className="row g-3">
                {/* 1. Site Selection */}
                <div className="col-12 col-md-4">
                  <SearchSelectInput
                    label="Project / O&M Site"
                    placeholder="Search Site..."
                    value={selectedSiteName}
                    onSelect={handleSelectSite}
                    options={sites}
                    onSearch={fetchSites}
                    isLoading={isLoadingSite}
                    errors={formErrors?.site}
                    showAddButton={false}
                    required={true}
                    className="mb-0"
                  />
                </div>

                {/* 2. Purchase Order Selection */}
                <div className="col-12 col-md-4">
                  <SearchSelectInput
                    label="Approved Purchase Order (PO)"
                    placeholder={!selectedSiteId ? "Select Site first..." : "Search Purchase Order..."}
                    value={selectedPoName}
                    onSelect={handleSelectPOOption}
                    options={poOptions}
                    onSearch={(query) => fetchPurchaseOrders(selectedSiteId, query)}
                    isLoading={isLoadingPo}
                    errors={formErrors?.po}
                    showAddButton={false}
                    required={true}
                    disabled={!selectedSiteId || loading}
                    className="mb-0"
                  />
                </div>

                {/* 3. GRN Date & Financial Year */}
                <div className="col-12 col-md-2">
                  <label className="form-label">
                    Inward Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={grnDate}
                    onChange={(e) => setGrnDate(e.target.value)}
                    max={moment().format("YYYY-MM-DD")}
                    required
                  />
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label">Financial Year</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={financialYear}
                    disabled
                  />
                </div>
              </div>

              {/* PO Info Bar */}
              {selectedPoDetail && (
                <div
                  className="mt-3 p-2 rounded-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", fontSize: "0.8rem" }}
                >
                  <div>
                    <span className="text-muted">Vendor: </span>
                    <strong className="text-dark">
                      {selectedPoDetail.vendor?.firmName || selectedPoDetail.vendor?.name || "Vendor Delivery"}
                    </strong>
                  </div>
                  <div>
                    <span className="text-muted">PO Date: </span>
                    <strong>{moment(selectedPoDetail.purchaseDate).format("DD MMM YYYY")}</strong>
                  </div>
                  <div>
                    <span className="text-muted">PO Total: </span>
                    <strong className="text-primary">
                      ₹{parseFloat(selectedPoDetail.totalAmount || 0).toLocaleString()}
                    </strong>
                  </div>
                  <div>
                    <span className="badge bg-success-subtle text-success border border-success">
                      Approved Supply PO
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="mt-2 text-muted fw-semibold small">Fetching PO items & asset requirements...</div>
              </div>
            )}

            {/* Empty State when no PO chosen */}
            {!loading && !selectedPoId && (
              <div className="card border-0 p-5 text-center bg-white rounded-3 shadow-sm">
                <span style={{ fontSize: "2.5rem" }}>📋</span>
                <h6 className="fw-bold mt-2 text-dark">No Purchase Order Selected</h6>
                <p className="text-muted small mb-0">
                  Select a Site and an approved Supply Purchase Order above to view items and begin inward receiving.
                </p>
              </div>
            )}

            {/* Items grouped by Expense Head */}
            {!loading && expenseHeads.length > 0 && (
              <div className="d-flex flex-column gap-3">
                {expenseHeads.map((head, hIdx) => (
                  <div key={hIdx} className="card border-0 shadow-sm rounded-3 overflow-hidden bg-white">
                    <div
                      className="card-header py-2 px-3 d-flex align-items-center justify-content-between"
                      style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
                    >
                      <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: "0.9rem" }}>
                        <span style={{ color: "#2563eb" }}>📁</span>
                        <span>{head.name}</span>
                      </div>
                      <span className="badge bg-light text-secondary border">
                        {head.items?.length || 0} PO Items
                      </span>
                    </div>

                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.825rem" }}>
                          <thead style={{ background: "#fafafa", color: "#64748b" }}>
                            <tr>
                              <th style={{ width: "30%" }}>Item / Asset Description</th>
                              <th style={{ width: "10%" }} className="text-center">Ordered</th>
                              <th style={{ width: "10%" }} className="text-center">Received</th>
                              <th style={{ width: "10%" }} className="text-center">Remaining</th>
                              <th style={{ width: "18%" }}>Receive Qty Now</th>
                              <th style={{ width: "22%" }} className="text-center">Machinery / Substitution Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {head.items.map((it, iIdx) => {
                              const hasSub = it.subItems && it.subItems.length > 0;
                              const isOver = parseFloat(it.receiveQty || 0) > it.remainingQty;
                              const unitsCount = it.machineUnits?.length || 0;
                              const hasUnitsFilled = unitsCount > 0;

                              return (
                                <React.Fragment key={iIdx}>
                                  <tr style={{ background: hasSub ? "#fffbeb" : it.isMachinery ? "#faf5ff" : "inherit" }}>
                                    {/* Description & Type Badge */}
                                    <td>
                                      <div className="fw-bold text-dark">{it.name}</div>
                                      <div className="d-flex align-items-center gap-2 mt-1">
                                        {it.isMachinery ? (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              fontWeight: 700,
                                              color: "#6d28d9",
                                              background: "#ede9fe",
                                              padding: "2px 6px",
                                              borderRadius: 4,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            🚜 Machinery & Fleet Asset
                                          </span>
                                        ) : (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              fontWeight: 700,
                                              color: "#059669",
                                              background: "#d1fae5",
                                              padding: "2px 6px",
                                              borderRadius: 4,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            📦 Consumable Material (Stock In)
                                          </span>
                                        )}
                                        <span className="text-muted" style={{ fontSize: "0.725rem" }}>
                                          Unit: {it.unit}
                                        </span>
                                      </div>
                                    </td>

                                    {/* Ordered */}
                                    <td className="text-center fw-semibold text-secondary">
                                      {it.orderedQty}
                                    </td>

                                    {/* Received */}
                                    <td className="text-center fw-semibold text-success">
                                      {it.receivedQty}
                                    </td>

                                    {/* Remaining */}
                                    <td className="text-center fw-bold" style={{ color: it.remainingQty > 0 ? "#d97706" : "#64748b" }}>
                                      {it.remainingQty}
                                    </td>

                                    {/* Received Now Input */}
                                    <td>
                                      <input
                                        type="number"
                                        step="any"
                                        min="0"
                                        max={it.remainingQty}
                                        disabled={hasSub || it.remainingQty <= 0}
                                        className={`form-control form-control-sm ${isOver ? "is-invalid" : ""}`}
                                        placeholder={hasSub ? "Substituted" : it.remainingQty <= 0 ? "Completed" : "Enter Qty"}
                                        value={it.receiveQty}
                                        onChange={(e) => handleItemQtyChange(hIdx, iIdx, e.target.value)}
                                      />
                                      {isOver && (
                                        <div className="text-danger mt-1" style={{ fontSize: "0.7rem" }}>
                                          Cannot exceed {it.remainingQty}
                                        </div>
                                      )}
                                    </td>

                                    {/* Action button: Machinery Form vs Substitute */}
                                    <td className="text-center">
                                      {it.isMachinery ? (
                                        <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            style={{
                                              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                                              borderColor: "#6d28d9",
                                              fontSize: "0.75rem",
                                              fontWeight: 700,
                                              padding: "3px 8px",
                                              borderRadius: 6,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 4,
                                              boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                                            }}
                                            onClick={() => openMachineryModal(hIdx, iIdx, it.name, it.itemCode)}
                                            disabled={!it.receiveQty || parseFloat(it.receiveQty) <= 0}
                                          >
                                            <span>🚜</span> Fill Machinery Form ({unitsCount})
                                          </button>

                                          {hasUnitsFilled && (
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-outline-secondary"
                                              onClick={() => toggleInlineUnits(hIdx, iIdx)}
                                              title={it.showInlineUnits ? "Hide inline view" : "Show inline view"}
                                              style={{ fontSize: "0.7rem", padding: "2px 5px", borderRadius: 4 }}
                                            >
                                              {it.showInlineUnits ? "▲ Collapse" : "▼ Details"}
                                            </button>
                                          )}
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          className={`btn btn-sm ${hasSub ? "btn-warning" : "btn-outline-primary"}`}
                                          style={{ fontSize: "0.725rem", padding: "2px 8px", borderRadius: 4 }}
                                          onClick={() => openSubItemDialog(hIdx, iIdx, it.name)}
                                          disabled={it.remainingQty <= 0}
                                        >
                                          {hasSub ? `Sub-Items (${it.subItems.length})` : "+ Substitute"}
                                        </button>
                                      )}
                                    </td>
                                  </tr>

                                  {/* Render Substituted Sub-Items if present */}
                                  {hasSub && (
                                    <tr>
                                      <td colSpan="6" style={{ background: "#fef3c7", padding: "0.75rem 1rem" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <div className="fw-bold small text-dark">
                                            DELIVERED SUB-ITEMS / SUBSTITUTES FOR "{it.name}":
                                          </div>
                                          <button
                                            type="button"
                                            className="btn btn-xs btn-outline-dark"
                                            style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                                            onClick={() => openSubItemDialog(hIdx, iIdx, it.name)}
                                          >
                                            + Add More
                                          </button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                          {it.subItems.map((sub, sIdx) => (
                                            <span
                                              key={sIdx}
                                              className="badge bg-white text-dark border d-inline-flex align-items-center gap-2 p-2 shadow-sm rounded-2"
                                            >
                                              <span className="fw-bold text-primary">{sub.name}</span>
                                              <span className="text-secondary">Qty: <strong>{sub.quantity}</strong></span>
                                              <button
                                                type="button"
                                                className="btn-close"
                                                style={{ fontSize: "0.6rem" }}
                                                onClick={() => handleRemoveSubItem(hIdx, iIdx, sIdx)}
                                                title="Remove"
                                              />
                                            </span>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}

                                  {/* INLINE MACHINERY ASSET EXPANSION (when toggled or open) */}
                                  {it.isMachinery && it.showInlineUnits && it.machineUnits && it.machineUnits.length > 0 && (
                                    <tr>
                                      <td colSpan="6" style={{ background: "#f5f3ff", padding: "1rem" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <div className="fw-bold text-purple d-flex align-items-center gap-2" style={{ color: "#6d28d9" }}>
                                            <span>🚜</span>
                                            <span>
                                              MACHINERY FLEET ONBOARDING &mdash; {it.machineUnits.length} Unit(s) to Register
                                            </span>
                                          </div>
                                          <button
                                            type="button"
                                            className="btn btn-xs btn-primary"
                                            style={{ background: "#7c3aed", borderColor: "#7c3aed", fontSize: "0.75rem" }}
                                            onClick={() => openMachineryModal(hIdx, iIdx, it.name, it.itemCode)}
                                          >
                                            ⤢ Open Full Form Modal
                                          </button>
                                        </div>

                                        <div className="d-flex flex-column gap-2 mt-2">
                                          {it.machineUnits.map((unit, uIdx) => (
                                            <div
                                              key={uIdx}
                                              className="card border-0 shadow-sm p-3 rounded-3"
                                              style={{ background: "#ffffff", borderLeft: "4px solid #7c3aed" }}
                                            >
                                              <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom">
                                                <div className="fw-bold small text-dark">
                                                  Unit #{uIdx + 1} &mdash; {it.name}
                                                </div>
                                                <span className="badge bg-purple-subtle" style={{ color: "#7c3aed", background: "#ede9fe" }}>
                                                  Auto-Linked to PO #{selectedPoDetail?.purchaseNumber || "PO"}
                                                </span>
                                              </div>

                                              <div className="row g-2">
                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Asset Tag / Code *
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={unit.assetNo}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "assetNo", e.target.value)
                                                    }
                                                    placeholder="e.g. EQ-SITE-1001"
                                                    required
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Reg / Machine # (Plate)
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={unit.machineNumber}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "machineNumber", e.target.value)
                                                    }
                                                    placeholder="e.g. RJ-14-EA-9901"
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Chassis / Frame #
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={unit.chesisNumber}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "chesisNumber", e.target.value)
                                                    }
                                                    placeholder="e.g. MA3ERF1234..."
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Engine Serial #
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={unit.engineNumber}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "engineNumber", e.target.value)
                                                    }
                                                    placeholder="e.g. ENG-88301"
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Meter Type
                                                  </label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    value={unit.readingType}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "readingType", e.target.value)
                                                    }
                                                  >
                                                    <option value="Hours">Hours (HR)</option>
                                                    <option value="Kilometers">Kilometers (KM)</option>
                                                  </select>
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Initial Meter Reading
                                                  </label>
                                                  <input
                                                    type="number"
                                                    step="any"
                                                    className="form-control form-control-sm"
                                                    value={unit.initialReading}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "initialReading", e.target.value)
                                                    }
                                                    placeholder="e.g. 0 or 15.4"
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Fuel / Power Type
                                                  </label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    value={unit.fuelType}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "fuelType", e.target.value)
                                                    }
                                                  >
                                                    <option value="Diesel">Diesel</option>
                                                    <option value="Petrol">Petrol</option>
                                                    <option value="Electric">Electric</option>
                                                    <option value="CNG">CNG</option>
                                                    <option value="Hybrid">Hybrid</option>
                                                    <option value="Not Applicable">Not Applicable</option>
                                                  </select>
                                                </div>

                                                <div className="col-md-3">
                                                  <label className="form-label mb-0" style={{ fontSize: "0.725rem", fontWeight: 600, color: "#64748b" }}>
                                                    Initial Status
                                                  </label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    value={unit.status}
                                                    onChange={(e) =>
                                                      handleMachineUnitFieldChange(hIdx, iIdx, uIdx, "status", e.target.value)
                                                    }
                                                  >
                                                    <option value="Operational">Operational</option>
                                                    <option value="Standby">Standby</option>
                                                    <option value="Under Maintenance">Under Maintenance</option>
                                                  </select>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uploads and Remarks Card */}
            <div className="card border-0 shadow-sm rounded-3 p-3 mt-4 bg-white">
              <h6 className="fw-bold text-dark mb-3">📎 Inward Proofs & Delivery Remarks</h6>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold small text-secondary">
                    Attach Delivery Challan / Invoice / Inspection Sheets
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <label
                      htmlFor="grn-file-upload"
                      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
                      style={{ cursor: "pointer", borderRadius: 6 }}
                    >
                      <span>📁</span> Choose Documents
                    </label>
                    <input
                      id="grn-file-upload"
                      type="file"
                      className="d-none"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                    />
                    <span className="text-muted small">
                      {files.length} document(s) selected
                    </span>
                  </div>

                  {/* File list preview */}
                  {files.length > 0 && (
                    <div className="mt-2 d-flex flex-column gap-1">
                      {files.map((f, fIdx) => (
                        <div
                          key={fIdx}
                          className="d-flex align-items-center justify-content-between p-2 rounded bg-light border small"
                        >
                          <span className="text-truncate fw-semibold text-dark" style={{ maxWidth: 300 }}>
                            📄 {f.name}
                          </span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-danger"
                            onClick={() => handleRemoveFile(fIdx)}
                            style={{ fontSize: "0.7rem", padding: "1px 6px" }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold small text-secondary">
                    Receipt Notes / Remarks
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="3"
                    placeholder="Enter delivery condition, gate pass #, vehicle driver notes, or quality inspection remarks..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </ModalBody>

          <ModalFooter style={{ background: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={submitting || !selectedPoId}
              style={{ background: "#059669", borderColor: "#059669", fontWeight: 700 }}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Generating GRN & Registering Assets...
                </>
              ) : (
                "📥 Complete Inward & Generate GRN"
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* DEDICATED MACHINERY UNIT CONFIGURATION MODAL */}
      <Modal isOpen={machineryModalOpen} toggle={() => setMachineryModalOpen(!machineryModalOpen)} size="lg" centered>
        <ModalHeader
          toggle={() => setMachineryModalOpen(!machineryModalOpen)}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setMachineryModalOpen(!machineryModalOpen)}
              aria-label="Close"
              style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
            />
          }
          style={{ background: "#4c1d95", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
            <span style={{ fontSize: "18px" }}>🚜</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: "15px" }}>
                Machinery Fleet Registration: {machineryModalTarget.itemName}
              </div>
              <div style={{ fontSize: "12px", color: "#c4b5fd", fontWeight: 400 }}>
                Configure asset identifiers and operational specs for {activeMachineryItem?.machineUnits?.length || 0} unit(s)
              </div>
            </div>
          </div>
        </ModalHeader>

        <ModalBody style={{ padding: "1.25rem", background: "#f8fafc" }}>
          {activeMachineryItem && activeMachineryItem.machineUnits && activeMachineryItem.machineUnits.length > 0 ? (
            <div>
              {/* Unit Stepper / Tabs if multiple units */}
              {activeMachineryItem.machineUnits.length > 1 && (
                <div className="d-flex gap-2 mb-3 overflow-auto pb-2">
                  {activeMachineryItem.machineUnits.map((u, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`btn btn-sm ${activeUnitTab === idx ? "btn-primary" : "btn-outline-secondary"}`}
                      style={{
                        borderRadius: 20,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        padding: "0.35rem 0.85rem",
                        whiteSpace: "nowrap",
                        background: activeUnitTab === idx ? "#7c3aed" : "transparent",
                        borderColor: activeUnitTab === idx ? "#7c3aed" : "#cbd5e1",
                      }}
                      onClick={() => setActiveUnitTab(idx)}
                    >
                      <span>Unit #{idx + 1}</span>
                      {u.machineNumber && <span className="ms-1 text-light">({u.machineNumber})</span>}
                    </button>
                  ))}
                </div>
              )}

              {/* Form for Active Unit */}
              {(() => {
                const currentUnit = activeMachineryItem.machineUnits[activeUnitTab] || activeMachineryItem.machineUnits[0];
                const uIdx = activeUnitTab;
                const { headIdx, itemIdx } = machineryModalTarget;

                if (!currentUnit) return null;

                return (
                  <div className="card border-0 shadow-sm p-3 bg-white rounded-3">
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                      <div>
                        <h6 className="fw-bold mb-0 text-dark">
                          Unit #{uIdx + 1} Specifications
                        </h6>
                        <span className="text-muted small">
                          Asset tag & identifiers for {activeMachineryItem.name}
                        </span>
                      </div>
                      <span className="badge bg-purple-subtle" style={{ color: "#7c3aed", background: "#ede9fe" }}>
                        Unit {uIdx + 1} of {activeMachineryItem.machineUnits.length}
                      </span>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Machine Name / Model <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.name}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "name", e.target.value)
                          }
                          required
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Asset Tag / Unique Code <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.assetNo}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "assetNo", e.target.value)
                          }
                          placeholder="e.g. JCB-SITE1-9821"
                          required
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Registration / Plate Number
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.machineNumber}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "machineNumber", e.target.value)
                          }
                          placeholder="e.g. RJ-14-EA-4491"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Chassis / Frame Number
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.chesisNumber}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "chesisNumber", e.target.value)
                          }
                          placeholder="e.g. MA3ERF12345678"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Engine Serial Number
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.engineNumber}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "engineNumber", e.target.value)
                          }
                          placeholder="e.g. ENG-88921"
                        />
                      </div>

                      <div className="col-12 col-md-3">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Meter Type
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={currentUnit.readingType}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "readingType", e.target.value)
                          }
                        >
                          <option value="Hours">Hours (HR)</option>
                          <option value="Kilometers">Kilometers (KM)</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-3">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Initial Meter Reading
                        </label>
                        <input
                          type="number"
                          step="any"
                          className="form-control form-control-sm"
                          value={currentUnit.initialReading}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "initialReading", e.target.value)
                          }
                          placeholder="0.0"
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Fuel / Power Type
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={currentUnit.fuelType}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "fuelType", e.target.value)
                          }
                        >
                          <option value="Diesel">Diesel</option>
                          <option value="Petrol">Petrol</option>
                          <option value="Electric">Electric</option>
                          <option value="CNG">CNG</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="Not Applicable">Not Applicable</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Make / Brand
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={currentUnit.make}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "make", e.target.value)
                          }
                          placeholder="e.g. JCB / Caterpillar / Tata"
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-bold small text-secondary mb-1">
                          Operational Status
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={currentUnit.status}
                          onChange={(e) =>
                            handleMachineUnitFieldChange(headIdx, itemIdx, uIdx, "status", e.target.value)
                          }
                        >
                          <option value="Operational">Operational</option>
                          <option value="Standby">Standby</option>
                          <option value="Under Maintenance">Under Maintenance</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              No machinery units configured yet.
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ background: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
          <Button color="secondary" onClick={() => setMachineryModalOpen(false)}>
            Close
          </Button>
          <Button
            color="primary"
            style={{ background: "#7c3aed", borderColor: "#7c3aed", fontWeight: 700 }}
            onClick={() => {
              setMachineryModalOpen(false);
              toast.success("Machinery specifications saved.");
            }}
          >
            ✓ Save Machine Specifications
          </Button>
        </ModalFooter>
      </Modal>

      {/* Sub-Item / Substitution Modal */}
      <Modal isOpen={subItemModalOpen} toggle={() => setSubItemModalOpen(!subItemModalOpen)} centered>
        <ModalHeader toggle={() => setSubItemModalOpen(!subItemModalOpen)}>
          Substitute Component for: <strong>{subItemTarget.itemName}</strong>
        </ModalHeader>
        <ModalBody style={{ overflow: "visible" }}>
          <div className="alert alert-info py-2 small mb-3">
            Add delivered sub-items/components. The main PO item will be marked fulfilled, and each component will be added directly into site inventory stock.
          </div>
          <div className="mb-3">
            <SearchSelectInput
              label="Select Substitute / Component Item"
              placeholder="Search Item from Master..."
              value={subItemSelection.itemName}
              onSelect={handleSelectSubItem}
              options={itemOptions}
              onSearch={fetchAvailableItems}
              isLoading={isLoadingItems}
              showAddButton={false}
              required={true}
              className="mb-0"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Delivered Quantity <span className="text-danger">*</span></label>
            <input
              type="number"
              step="any"
              min="0.01"
              className="form-control"
              placeholder="e.g. 10"
              value={subItemSelection.quantity}
              onChange={(e) => setSubItemSelection({ ...subItemSelection, quantity: e.target.value })}
              required
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSubItemModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleAddSubItem}>
            + Add Component
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}